/**
 * Phase 1 Contest Judging — Admin master ON/OFF toggle.
 *
 * Mirrors ContestFlagToggle.jsx (Contest Center master) and the
 * Spotlight A&R platform master switch. Reads the current state from
 * the existing /api/homepage/config endpoint and writes via the
 * single-purpose /api/admin/homepage/contest-judging-toggle endpoint.
 *
 * Turning OFF never deletes judges, reviews, rubrics, entries,
 * results, or winners — the Phase 1 routes simply return
 * 404 judging_module_disabled while the flag is off. Turning ON only
 * makes the already-built judging routes available; it does NOT
 * change any contest's status.
 */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

export const ContestJudgingFlagToggle = () => {
  const [enabled, setEnabled] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/homepage/config").then((r) => {
      const on = !!r.data?.contests_meta?.judging_module?.enabled;
      setEnabled(on);
    }).catch(() => setEnabled(false));
  }, []);

  const toggle = async () => {
    if (enabled == null) return;
    const next = !enabled;
    const msg = next
      ? "Turn the Contest Judging Module ON?\n\nAssigned judges can access /judge and begin scoring entries. This does NOT change any contest's status — you still click 'Begin Judging' per contest."
      : "Turn the Contest Judging Module OFF?\n\nExisting judge assignments, rubrics, reviews, entries, and winners are preserved. Judging routes return 404 globally until re-enabled.";
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      const r = await api.put("/admin/homepage/contest-judging-toggle",
        { enabled: next });
      const val = !!r.data?.contests?.judging_module?.enabled;
      setEnabled(val);
      toast.success(val
        ? "Contest Judging Module: ON"
        : "Contest Judging Module: OFF");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally { setSaving(false); }
  };

  if (enabled == null) {
    return (
      <div
        className="text-xs text-gray-500"
        data-testid="admin-contest-judging-flag-loading"
      >Loading judging flag…</div>
    );
  }

  return (
    <div
      className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2"
      data-testid="admin-contest-judging-flag-panel"
    >
      <span
        className={`text-[10px] px-2 py-1 rounded-full uppercase tracking-wider font-black ${enabled ? "bg-emerald-500/20 text-emerald-200 border border-emerald-500/40" : "bg-white/10 text-gray-300 border border-white/15"}`}
        data-testid="admin-contest-judging-flag-status"
      >
        Contest Judging · {enabled ? "ON" : "OFF"}
      </span>
      <span className="text-[11px] text-gray-400 hidden sm:inline">
        Enables /judge inbox + per-contest judging workspace. Does not
        change any contest's status.
      </span>
      <Button
        onClick={toggle}
        disabled={saving}
        className={enabled
          ? "bg-rose-600 hover:bg-rose-500 text-white font-black text-xs px-3 py-1 h-auto ml-auto"
          : "bg-white text-black hover:bg-amber-200 font-black text-xs px-3 py-1 h-auto ml-auto"}
        data-testid="admin-contest-judging-flag-toggle-btn"
      >
        {saving ? "…" : enabled ? "Turn OFF" : "Turn ON"}
      </Button>
    </div>
  );
};

export default ContestJudgingFlagToggle;
